import {Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {DomSanitizer} from '@angular/platform-browser';
import {AbstractControl, FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {MaterialModule} from '../../../../material.module';
import {ChatbotsService} from '../../../../services/objects/chatbots.service';
import {FigmaProductCardMapping, FigmaProductCardMaster, FigmaStylingMode, isFigmaFileUrl, isFigmaPageUrl} from '../../../../models/figma-product-card.model';
import {FigmaDesignSnapshot, FigmaDesignView} from '../../../../models/figma-chat-design.model';
import {environment} from '../../../../../environments/environment';

@Component({
  selector: 'app-figma-product-card-settings',
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule, DatePipe],
  templateUrl: './figma-product-card-settings.component.html',
  styleUrl: './figma-product-card-settings.component.scss'
})
export class FigmaProductCardSettingsComponent implements OnChanges, OnDestroy {
  @Input() clientId = '';
  @Input() chatbotId = '';
  @Output() modeChange = new EventEmitter<FigmaStylingMode>();
  @Output() snapshotChange = new EventEmitter<FigmaDesignSnapshot | null>();

  private readonly fb = inject(FormBuilder);
  private readonly service = inject(ChatbotsService);
  private readonly sanitizer = inject(DomSanitizer);
  private previewFrame: HTMLIFrameElement | null = null;
  private readonly previewUrlValue = this.buildPreviewUrl();
  readonly previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrlValue);
  private readonly previewMessage = (event: MessageEvent): void => {
    if (!this.previewFrame?.contentWindow || event.source !== this.previewFrame.contentWindow
      || event.origin !== new URL(this.previewUrlValue).origin) return;
    if (event.data?.type === 'shine.figma-preview.ready.v1') this.sendPreview();
    if (event.data?.type === 'shine.figma-preview.rendered.v1') this.previewStatus = this.snapshot?.preview
      ? 'Oppdatert med Figma-eksempler og chatens visning.' : 'Hent designet på nytt for å oppdatere forhåndsvisningen.';
    if (event.data?.type === 'shine.figma-preview.rejected.v1') this.previewStatus = 'Forhåndsvisningen avviste ugyldige designdata.';
  };
  private requests = new Subscription();
  private readonly changes = new Subscription();
  private generation = 0;
  private draftRevision = '';
  private masterRevision = '';
  private masterMappings: FigmaProductCardMapping[] = [];
  private selectedNodeId = '';

  readonly draftForm = this.fb.nonNullable.group({
    mode: this.fb.nonNullable.control<FigmaStylingMode>('standard'),
    designUrl: ['', (control: AbstractControl) => isFigmaPageUrl(control.value) ? null : {figmaPageUrl: true}]
  });
  readonly masterForm = this.fb.nonNullable.group({
    masterTemplateUrl: ['', (control: AbstractControl) => isFigmaFileUrl(control.value) ? null : {figmaFileUrl: true}]
  });

  loaded = false;
  loading = false;
  savingDraft = false;
  savingMaster = false;
  canManageMaster = false;
  error = '';
  status = '';
  refreshing = false;
  canRefresh = false;
  localRuntimeEnabled = false;
  connected = false;
  snapshot: FigmaDesignSnapshot | null = null;
  selectedView = 'Desktop/Start';
  previewStatus = '';
  get currentView(): FigmaDesignView | undefined { return this.snapshot?.views.find(view => view.name === this.selectedView); }

  constructor() {
    window.addEventListener('message', this.previewMessage);
    this.changes.add(this.draftForm.controls.mode.valueChanges.subscribe(mode => this.modeChange.emit(mode)));
    this.changes.add(this.draftForm.controls.designUrl.valueChanges.subscribe(() => { this.selectedNodeId = ''; }));
  }

  get experimental(): boolean { return this.draftForm.controls.mode.value === 'experimental'; }

  ngOnChanges(): void { this.load(); }
  ngOnDestroy(): void { this.requests.unsubscribe(); this.changes.unsubscribe(); window.removeEventListener('message', this.previewMessage); }

  load(): void {
    this.requests.unsubscribe();
    this.requests = new Subscription();
    const generation = ++this.generation;
    this.refreshing = this.canRefresh = this.connected = this.localRuntimeEnabled = false;
    this.snapshot = null;
    this.selectedView = 'Desktop/Start';
    this.snapshotChange.emit(null);
    this.previewFrame = null;
    this.previewStatus = '';
    this.loaded = this.loading = this.savingDraft = this.savingMaster = this.canManageMaster = false;
    this.error = this.status = this.draftRevision = this.masterRevision = this.selectedNodeId = '';
    this.masterMappings = [];
    this.masterForm.reset({masterTemplateUrl: ''});
    this.draftForm.reset({mode: 'standard', designUrl: ''});
    this.draftForm.disable({emitEvent: false});
    if (!this.clientId || !this.chatbotId) return;
    this.loading = true;
    this.requests.add(this.service.getFigmaProductCardEditor(this.clientId, this.chatbotId).subscribe({
      next: editor => {
        if (generation !== this.generation) return;
        this.loading = false;
        this.loaded = true;
        this.draftRevision = editor.draft.revision;
        this.draftForm.reset({mode: editor.draft.mode === 'experimental' ? 'experimental' : 'standard', designUrl: editor.draft.designUrl});
        this.draftForm.enable({emitEvent: false});
        this.selectedNodeId = editor.draft.selectedNodeId;
        this.canManageMaster = editor.canManageMaster === true && !!editor.master;
        this.canRefresh = editor.canRefresh === true;
        this.connected = editor.importStatus === 'connected';
        this.snapshot = editor.snapshot ?? null;
        this.snapshotChange.emit(this.snapshot);
        this.localRuntimeEnabled = editor.localRuntimeEnabled === true;
        if (this.canManageMaster && editor.master) this.setMaster(editor.master);
      },
      error: () => { if (generation === this.generation) { this.loading = false; this.error = 'Figma-oppsettet kunne ikke lastes. Standard-oppsettet er tilgjengelig.'; } }
    }));
  }

  saveDraft(): void {
    if (!this.loaded || this.savingDraft || this.refreshing || this.draftForm.invalid) return;
    this.savingDraft = true;
    this.draftForm.disable({emitEvent: false});
    this.error = this.status = '';
    // Capture the complete identity/value; changing client cancels the subscription.
    this.requests.add(this.service.saveFigmaProductCardDraft(this.clientId, this.chatbotId, {
      ...this.draftForm.getRawValue(), selectedNodeId: this.selectedNodeId, revision: this.draftRevision
    }).subscribe({
      next: draft => {
        this.savingDraft = false;
        this.draftForm.enable({emitEvent: false});
        this.draftRevision = draft.revision;
        this.selectedNodeId = draft.selectedNodeId;
        this.draftForm.markAsPristine();
        this.status = this.localRuntimeEnabled
          ? `Valget er lagret. Chatten oppdateres til ${draft.mode === 'experimental' ? 'det importerte Figma-designet' : 'Standard'}.`
          : 'Figma-valget er lagret. Chatten bruker fortsatt Standard-styling.';
      },
      error: (error: {status?: number}) => {
        this.savingDraft = false;
        this.draftForm.enable({emitEvent: false});
        this.error = this.saveError(error.status);
      }
    }));
  }

  refreshDesign(): void {
    if (!this.canRefresh || this.refreshing || this.savingDraft || this.draftForm.invalid || !this.draftForm.controls.designUrl.value) return;
    this.refreshing = true;
    this.error = this.status = '';
    this.draftForm.disable({emitEvent: false});
    this.requests.add(this.service.refreshFigmaDesign(this.clientId, this.chatbotId, {
      designUrl: this.draftForm.getRawValue().designUrl, revision: this.draftRevision
    }).subscribe({
      next: result => {
        this.refreshing = false;
        this.draftForm.enable({emitEvent: false});
        this.draftRevision = result.draft.revision;
        this.selectedNodeId = result.draft.selectedNodeId;
        this.snapshot = result.snapshot;
        this.snapshotChange.emit(this.snapshot);
        this.draftForm.reset({mode: result.draft.mode, designUrl: result.draft.designUrl});
        this.status = this.localRuntimeEnabled
          ? 'Designet er hentet og validert. Chatten oppdateres. Kontroller forhåndsvisning og merknader.'
          : 'Designutkastet er hentet og validert. Kontroller forhåndsvisning og merknader. Chatten bruker fortsatt Standard-styling.';
        this.sendPreview();
      },
      error: (failure: {status?: number; error?: {error?: string}}) => {
        this.refreshing = false;
        this.draftForm.enable({emitEvent: false});
        this.error = typeof failure.error?.error === 'string' ? failure.error.error.slice(0,1000)
          : failure.status === 409 ? this.saveError(409)
          : 'Importen kunne ikke bekreftes. Forrige designutkast er beholdt. Last inn på nytt for å kontrollere status.';
      }
    }));
  }

  onPreviewLoad(frame: HTMLIFrameElement): void { this.previewFrame = frame; this.sendPreview(); }
  selectView(name: string): void { this.selectedView = name; this.sendPreview(); }
  private sendPreview(): void {
    if (!this.currentView || !this.previewFrame?.contentWindow) return;
    this.previewStatus = 'Oppdaterer forhåndsvisning…';
    const content = this.snapshot?.preview?.views.find(view => view.name === this.selectedView) ?? null;
    const ids = new Set([content?.logo, content?.feedback, ...(content?.products.map(item => item.image) ?? []), ...(content?.quickReplies.map(item => item.image) ?? [])]);
    const images = Object.fromEntries(Object.entries(this.snapshot?.preview?.images ?? {}).filter(([id]) => ids.has(id)));
    this.previewFrame.contentWindow.postMessage({type: 'shine.figma-preview.configure.v1', view: this.currentView, content, images}, new URL(this.previewUrlValue).origin);
  }
  private buildPreviewUrl(): string {
    const url = new URL(environment.frontendDummy || environment.widgetScriptUrl || window.location.origin, window.location.origin);
    const base = url.pathname.replace(/(?:generic|chatbot)(?:\/index\.html|\.js)?\/?$/i, '');
    url.pathname = `${base.replace(/\/?$/, '/')}product-card-preview/index.html`;
    url.search = '';
    url.hash = '';
    url.searchParams.set('parentOrigin', window.location.origin);
    url.searchParams.set('figmaPreview', '1');
    // Escape HTML cached before the widget introduced hashed assets/cache headers.
    url.searchParams.set('assets', 'content-hash-v1');
    return url.toString();
  }

  saveMaster(): void {
    if (!this.canManageMaster || this.savingMaster || this.masterForm.invalid) return;
    this.savingMaster = true;
    this.masterForm.disable({emitEvent: false});
    this.error = this.status = '';
    this.requests.add(this.service.saveFigmaProductCardMaster(this.clientId, this.chatbotId, {
      // Preserve stored mappings when updating the master URL; their editor has been retired.
      ...this.masterForm.getRawValue(), mappings: this.masterMappings, revision: this.masterRevision
    }).subscribe({
      next: master => { this.savingMaster = false; this.setMaster(master); this.status = 'Felles master er lagret.'; },
      error: (error: {status?: number}) => {
        this.savingMaster = false;
        this.masterForm.enable({emitEvent: false});
        this.error = this.saveError(error.status);
      }
    }));
  }

  private setMaster(master: FigmaProductCardMaster): void {
    this.masterForm.enable({emitEvent: false});
    this.masterRevision = master.revision;
    this.masterMappings = master.mappings;
    this.masterForm.controls.masterTemplateUrl.setValue(master.masterTemplateUrl);
    this.masterForm.markAsPristine();
  }
  private saveError(status?: number): string {
    return status === 409 ? 'Oppsettet ble endret av noen andre. Last inn på nytt før du lagrer.'
      : status === 403 ? 'Du har ikke tilgang til å lagre dette oppsettet.'
      : 'Lagringen kunne ikke bekreftes. Utkastet står fortsatt i skjemaet. Last inn på nytt for å kontrollere lagret versjon.';
  }
}
